import { makeStyles } from '@material-ui/styles'
import { listUserTenants } from 'account/components/userManagement/tenants/new-actions'
import { userTenantsSelector } from 'account/components/userManagement/tenants/selectors'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import clsx from 'clsx'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import DocumentMeta from 'core/components/DocumentMeta'
import SimpleLink from 'core/components/SimpleLink'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import CreateButton from 'core/components/buttons/CreateButton'
import DotValueCell from 'core/components/listTable/cells/DotValueCell'
import ListContainer from 'core/containers/ListContainer'
import Text from 'core/elements/Text'
import ToggleSwitch from 'core/elements/ToggleSwitch'
import Badge from 'core/elements/badge/Badge'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import getGridDialogButton from 'core/elements/grid/helpers/getGridDialogButton'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import {
  GridBatchActionSpec,
  GridDropdownBatchActionGroup,
} from 'core/elements/grid/hooks/useGridSelectableRows'
import ColumnInfoTooltip from 'core/elements/tooltip/ColumnInfoTooltip'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { isReadonlyUser } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import { listResmgrHosts } from 'openstack/components/infrastructure/actions'
import { listClusterHostGroups } from 'openstack/components/infrastructure/clusters/actions'
import { clusterHostGroupsSelectorSimple } from 'openstack/components/infrastructure/clusters/selectors'
import { IHostDetailsPageTabs } from 'openstack/components/infrastructure/hosts/host-details/model'
import { resmgrHostsByIdSelector } from 'openstack/components/infrastructure/selectors'
import { IVmDetailsPageTabs } from 'openstack/components/vms/vm-details/model'
import { getCurrentUser, humanReadableSize } from 'openstack/helpers'
import { insertAll, pick } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { formatTableDate } from 'utils/misc'
import { getBlueprints } from '../../infrastructure/actions'
import AcceptVolumeTransferDialog from './AcceptVolumeTransferDialog'
import AttachVolumeDialog from './AttachVolumeDialog'
import CancelTransferDialog from './CancelTransferDialog'
import CreateVolumeModal from './CreateVolumeModal'
import DeleteVolumesDialog from './DeleteVolumesDialog'
import DetachVolumeDialog from './DetachVolumeDialog'
import EditVolumeDialog from './EditVolumeDialog'
import EditVolumeMetadataModal from './EditVolumeMetadataModal'
import ExtendVolumeDialog from './ExtendVolumeDialog'
import MigrateVolumeDialog from './MigrateVolumeDialog'
import RetypeVolumeDialog from './RetypeVolumeDialog'
import SetStateVolumeDialog from './SetStateVolumeDialog'
import SnapshotVolumeDialog from './SnapshotVolumeDialog'
import TransferVolumeDialog from './TransferVolumeDialog'
import UploadVolumeAsImageDialog from './UploadVolumeAsImageDialog'
import VolumeErrorModal from './VolumeErrorModal'
import { deleteVolume, listVolumes } from './actions'
import { VOLUME_STATES_TOOLTIPS } from './constants'
import { volumesSelector } from './selectors'
import { VOLUME_ACTION_TOOLTIPS } from './tooltipConstants'
import { VolumeSourceCell } from './volume-details/VolumeSourceCell'
import { IVolumeDetailsPageTabs } from './volume-details/model'
// Pagination
import PollingData from 'core/components/PollingData'
import { FilterConfig } from 'core/components/server-side-pagination/ServerSideFilters'
import ServerSidePagination from 'core/components/server-side-pagination/ServerSidePagination'
import ServerSideSearch, {
  SearchFieldOption,
  ServerSideSearchParams,
} from 'core/components/server-side-pagination/ServerSideSearch'
import { SortFieldOption } from 'core/components/server-side-pagination/ServerSideSort'
import useVolumesPagination from './useVolumesPagination'

type ModelDataKey = DataKeys.OpenstackVolumes
type SelectorModel = ArrayElement<ReturnType<typeof volumesSelector>>
type ActionParams = InferActionParams<typeof listVolumes>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {}

const defaultParams: TablePrefsParams = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

// Search field options for ports
const VOLUMES_SEARCH_FIELDS: SearchFieldOption[] = [
  { value: 'name', label: 'Name' },
  { value: 'id', label: 'UUID' },
  { value: 'user_id', label: 'User ID' },
  { value: 'backend', label: 'Volume Backend' },
  { value: 'volume_type', label: 'Volume Type' },
]

const getVolumeFilterConfigs: (any) => FilterConfig[] = ({ allTenants, tenants, clusters }) => {
  const filters = [
    {
      key: 'cluster_name',
      label: 'Cluster',
      options: [
        { value: 'all', label: 'All' },
        ...clusters.map((cluster) => ({ value: cluster.name, label: cluster.name })),
      ],
      defaultValue: 'all',
    },
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'all', label: 'All' },
        { value: 'available', label: 'Available' },
        { value: 'in-use', label: 'In-Use' },
        { value: 'maintenance', label: 'Maintenance' },
        { value: 'awaiting-transfer', label: 'Awaiting Transfer' },
        { value: 'error', label: 'Error' },
        { value: 'reserved', label: 'Reserved' },
        { value: 'creating', label: 'Creating' },
        { value: 'attaching', label: 'Attaching' },
        { value: 'detaching', label: 'Detaching' },
        { value: 'deleting', label: 'Deleting' },
        { value: 'error_deleting', label: 'Error Deleting' },
        { value: 'extending', label: 'Extending' },
        { value: 'retyping', label: 'Retyping' },
        { value: 'backing-up', label: 'Backing Up' },
        { value: 'restoring-backup', label: 'Restoring Backup' },
        { value: 'error_backing-up', label: 'Error Backing Up' },
        { value: 'error_restoring', label: 'Error Restoring' },
        { value: 'error_extending', label: 'Error Extending' },
        { value: 'downloading', label: 'Downloading' },
        { value: 'uploading', label: 'Uploading' },
      ],
      defaultValue: 'all',
    },
    {
      key: 'bootable',
      label: 'Bootable',
      options: [
        { value: 'all', label: 'All' },
        { value: 'true', label: 'True' },
        { value: 'false', label: 'False' },
      ],
      defaultValue: 'all',
    },
    {
      key: 'multiattach',
      label: 'Multiattach',
      options: [
        { value: 'all', label: 'All' },
        { value: 'true', label: 'True' },
        { value: 'false', label: 'False' },
      ],
      defaultValue: 'all',
    },
  ]
  if (allTenants) {
    return [
      {
        key: 'tenant_id',
        label: 'Tenant',
        options: [
          { value: 'all', label: 'All' },
          ...tenants.map((tenant) => ({ value: tenant.id, label: tenant.name })),
        ],
        defaultValue: 'all',
      },
      ...filters,
    ]
  }
  return filters
}

// Sort field options for ports
const VOLUMES_SORT_OPTIONS: SortFieldOption[] = [
  { value: 'created_at', label: 'Created' },
  // { value: 'updated_at', label: 'Updated' },
  { value: 'name', label: 'Name' },
  { value: 'size', label: 'Size' },
]

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'OpenstackVolumes',
  listTablePrefs,
)

// const statusDisplay = {
//   ['awaiting-transfer']: 'Awaiting Transfer',
// }

const getBadgeVariant = (status) => {
  return (
    {
      creating: 'warning',
      available: 'success',
      reserved: 'primary',
      attaching: 'warning',
      detaching: 'warning',
      'in-use': 'success',
      maintenance: 'primary',
      deleting: 'warning',
      'awaiting-transfer': 'warning',
      error: 'error',
      error_deleting: 'error',
      'backing-up': 'warning',
      'restoring-backup': 'warning',
      'error_backing-up': 'error',
      error_restoring: 'error',
      error_extending: 'error',
      downloading: 'warning',
      uploading: 'warning',
      retyping: 'warning',
      extending: 'warning',
    }[status] || 'unknown'
  )
}

const getBadgeText = (status) => {
  const text =
    {
      creating: 'Creating',
      available: 'Available',
      reserved: 'Reserved',
      attaching: 'Attaching',
      detaching: 'Detaching',
      'in-use': 'In-Use',
      maintenance: 'Maintenance',
      deleting: 'Deleting',
      'awaiting-transfer': 'Awaiting Transfer',
      error: 'Error',
      error_deleting: 'Error Deleting',
      'backing-up': 'Backing Up',
      'restoring-backup': 'Restoring Backup',
      'error_backing-up': 'Error Backing Up',
      error_restoring: 'Error Restoring',
      error_extending: 'Error Extending',
      downloading: 'Downloading',
      uploading: 'Uploading',
      retyping: 'Retyping',
      extending: 'Extending',
    }[status] || status
  return text
}

export const StatusCellComponent = ({ value, item, setErrorVolume = null }) => {
  const classes = useStyles()
  const migrationStatus = item?.migration_status
  const exposeError = setErrorVolume && value === 'error'

  return (
    <div className={clsx(classes.statusCell, exposeError && classes.error)}>
      <Badge
        variant={getBadgeVariant(value)}
        text={getBadgeText(value)}
        onClick={(e) => {
          e.stopPropagation()
          if (exposeError) {
            setErrorVolume(item)
          }
        }}
      />
      {migrationStatus && (
        <Text variant="body2" className={classes.migrationStatusCell}>
          ({migrationStatus})
        </Text>
      )}
    </div>
  )
}

export const volumeColumns = ({
  hostsById,
  isAdmin,
  allTenants,
  setErrorVolume,
}): GridViewColumn<SelectorModel>[] => {
  const columns = [
    {
      key: 'name',
      label: 'Name',
      // width: 'medium',
      CellComponent: createGridLinkCell({
        routeToFn: ({ id, name }) =>
          !!name
            ? routeManager.routes.openstack.volumeDetails.path({
                id,
                tab: IVolumeDetailsPageTabs.Overview,
              })
            : '',
      }),
    },
    {
      key: 'id',
      label: 'UUID',
      display: false,
    },
    {
      key: 'status',
      label: (
        <TitleWithInfoIcon
          titleText="Status"
          tooltipMsg={<ColumnInfoTooltip statuses={VOLUME_STATES_TOOLTIPS} />}
        />
      ),
      // render: (status) => statusDisplay[status] || status,
      CellComponent: (props) => <StatusCellComponent {...props} setErrorVolume={setErrorVolume} />,
    },
    {
      key: 'tenantName',
      label: 'Tenant',
      visible: allTenants,
    },
    {
      key: 'volume_type',
      label: 'Type',
    },
    {
      key: 'description',
      label: 'Description',
    },
    {
      key: 'attachedVm',
      label: 'VM',
      render: (val) =>
        val ? (
          <SimpleLink
            src={routeManager.routes.openstack.vmDetails.path({
              id: val?.id,
              tab: IVmDetailsPageTabs.Overview,
            })}
          >
            {val?.name || val?.id}
          </SimpleLink>
        ) : null,
    },
    {
      key: 'device',
      label: 'Device',
    },
    {
      key: 'snapshots',
      label: 'Snapshots',
      render: (snapshots) => snapshots?.length,
    },
    {
      key: 'size',
      label: 'Size',
      render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
    },
    {
      key: 'bootable',
      label: 'Bootable',
      // bootable is string true / false
      render: (val) => <DotValueCell value={val === 'true'} />,
    },
    {
      key: 'multiattach',
      label: 'Multiattach',
      // multiattach is boolean
      render: (val) => <DotValueCell value={val} />,
    },
    {
      key: 'source',
      label: (
        <TitleWithInfoIcon
          titleText="Source"
          tooltipMsg={'The resource used to create this volume. Blank if no resource used'}
        />
      ),
      CellComponent: VolumeSourceCell,
    },
    {
      key: 'created_at',
      label: 'Created',
      formatFn: (value) => (value ? formatTableDate(value) : null),
    },
    {
      key: 'updated_at',
      label: 'Updated At',
      display: false,
      formatFn: (value) => (value ? formatTableDate(value) : null),
    },
    {
      key: 'metadata',
      label: 'Metadata',
      CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
    },
  ]
  if (isAdmin()) {
    // @ts-ignore
    return insertAll(
      3,
      [
        {
          key: 'hostId',
          label: 'Management Host',
          render: (hostId) =>
            hostsById?.[hostId]?.name || hostId ? (
              <SimpleLink
                src={routeManager.routes.openstack.hostDetails.path({
                  id: hostId,
                  tab: IHostDetailsPageTabs.Overview,
                })}
              >
                {hostsById?.[hostId]?.name || hostId}
              </SimpleLink>
            ) : null,
        },
        {
          key: 'backend',
          label: 'Volume Backend',
        },
      ],
      columns,
    )
  }
  return columns
}

export default function VolumesListPage() {
  const classes = useStyles()
  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs
  const [allTenants, setAllTenants] = useState(false)
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)

  // Server-side pagination with search/filter/sort for volumes
  const {
    paginatedData: volumes,
    loading: loadingVolumes,
    handleRefresh: reloadVolumes,
    handlePoll,
    polling,
    handleSearch,
    handleClearSearch,
    searchParams,
    paginationProps,
  } = useVolumesPagination({ allTenants })

  // Handle search/filter/sort from the search component
  const onSearch = useCallback(
    (params: ServerSideSearchParams) => {
      handleSearch({
        searchField: params.searchField,
        searchValue: params.searchValue,
        filters: params.filters,
        sortKey: params.sortKey,
        sortDir: params.sortDir,
      })
    },
    [handleSearch],
  )

  // Handle clear search
  const onClear = useCallback(() => {
    handleClearSearch()
  }, [handleClearSearch])

  const { loading: loadingResmgrHosts } = useListAction(listResmgrHosts, {
    params,
  })
  const hostsById = useSelector(resmgrHostsByIdSelector)

  const { loading: loadingTenants } = useListAction(listUserTenants)
  const tenants = useSelector(userTenantsSelector)

  const { loading: loadingClusters } = useListAction(listClusterHostGroups)
  const clusters = useSelector(clusterHostGroupsSelectorSimple)

  const [selectedVolume, setSelectedVolume] = useState(null)
  const [showAcceptVolumeTransferDialog, setShowAcceptVolumeTransferDialog] = useState(false)
  const [showEditVolumeDialog, setShowEditVolumeDialog] = useState(false)
  const [showSnapshotVolumeDialog, setShowSnapshotVolumeDialog] = useState(false)
  const [showUploadVolumeDialog, setShowUploadVolumeDialog] = useState(false)
  const [showAttachVolumeDialog, setShowAttachVolumeDialog] = useState(false)
  const [showDetachVolumeDialog, setShowDetachVolumeDialog] = useState(false)
  const [showExtendVolumeDialog, setShowExtendVolumeDialog] = useState(false)
  const [showRetypeVolumeDialog, setShowRetypeVolumeDialog] = useState(false)
  const [showDeleteVolumeDialog, setShowDeleteVolumeDialog] = useState(false)
  const [showTransferVolumeDialog, setShowTransferVolumeDialog] = useState(false)
  const [showCancelTransferVolumeDialog, setShowCancelTransferVolumeDialog] = useState(false)
  const [showEditVolumeMetadataModal, setShowEditVolumeMetadataModal] = useState(false)
  const [showMigrateVolumeDialog, setShowMigrateVolumeDialog] = useState(false)
  const [showSetStateVolumeDialog, setShowSetStateVolumeDialog] = useState(false)
  const [errorVolume, setErrorVolume] = useState(null)

  const [blueprintDetails, setBlueprintDetails] = useState({})
  const { userId } = getCurrentUser()

  useEffect(() => {
    const getDriverDetails = async () => {
      const blueprints = await getBlueprints()
      setBlueprintDetails(blueprints[0]?.storageBackends)
    }

    getDriverDetails()
  }, [])

  const batchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      cond: (volumes) => {
        return volumes?.length === 1
      },
      disabledTooltip: VOLUME_ACTION_TOOLTIPS.EDIT_SINGLE,
      icon: 'edit',
      iconOnly: true,
      tooltip: 'Edit',
      label: 'Edit',
      handleAction: (volumes) => {
        setSelectedVolume(volumes[0] ?? null)
        setShowEditVolumeDialog(true)
      },
    },
    {
      cond: (volumes) => {
        return volumes?.length === 1
      },
      disabledTooltip: VOLUME_ACTION_TOOLTIPS.SNAPSHOT_SINGLE,
      icon: 'camera',
      iconOnly: true,
      tooltip: 'Snapshot',
      label: 'Snapshot',
      handleAction: (volumes) => {
        setSelectedVolume(volumes[0] ?? null)
        setShowSnapshotVolumeDialog(true)
      },
    },
  ]

  const dropdownBatchActions: GridDropdownBatchActionGroup<SelectorModel>[] = useMemo(
    () => [
      {
        // icon: 'power-off',
        label: 'Actions',
        actions: [
          {
            label: 'Edit Metadata',
            icon: 'tags',
            cond: (volumes) => volumes?.length === 1,
            disabledTooltip: VOLUME_ACTION_TOOLTIPS.EDIT_METADATA_SINGLE,
            BatchActionButton: getGridDialogButton(
              EditVolumeMetadataModal,
              { reloadVolumes },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            cond: (volumes) => volumes?.length === 1,
            icon: 'upload',
            label: 'Upload as Image',
            disabledTooltip: VOLUME_ACTION_TOOLTIPS.UPLOAD_IMAGE_SINGLE,
            BatchActionButton: getGridDialogButton(
              UploadVolumeAsImageDialog,
              { reloadVolumes },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            cond: (volumes) => volumes?.length === 1 && volumes?.[0]?.status === 'available',
            icon: 'plus-square',
            label: 'Attach Volume',
            disabledTooltip: (volumes) =>
              volumes?.length > 1
                ? VOLUME_ACTION_TOOLTIPS.ATTACH_SINGLE
                : volumes?.[0]?.status !== 'available'
                ? VOLUME_ACTION_TOOLTIPS.ATTACH_AVAILABLE_STATUS
                : '',
            BatchActionButton: getGridDialogButton(
              AttachVolumeDialog,
              { reloadVolumes },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            cond: (volumes) => volumes?.length === 1 && volumes?.[0]?.status === 'in-use',
            icon: 'minus-square',
            label: 'Detach Volume',
            disabledTooltip: (volumes) =>
              volumes?.length > 1
                ? VOLUME_ACTION_TOOLTIPS.DETACH_SINGLE
                : volumes?.[0]?.status !== 'in-use'
                ? VOLUME_ACTION_TOOLTIPS.DETACH_IN_USE_STATUS
                : '',
            BatchActionButton: getGridDialogButton(
              DetachVolumeDialog,
              { reloadVolumes },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            cond: (volumes) => {
              if (volumes?.length !== 1) {
                return false
              }
              const volumeHostAtt = volumes?.[0]?.['os-vol-host-attr:host'] || ''
              const volumeType = volumeHostAtt?.split('#')?.[1]
              const volumeTypeConfig = volumeHostAtt?.split('@')?.[1]?.split('#')?.[0]
              const volumeDriver = blueprintDetails?.[volumeType]?.[volumeTypeConfig]?.driver || ''

              return (
                volumes[0]?.status === 'available' ||
                (volumes[0]?.status === 'in-use' && volumeDriver !== 'NFS')
              )
            },
            icon: 'square-up',
            label: 'Extend Volume',
            disabledTooltip: (volumes) => {
              const volumeHostAtt = volumes?.[0]?.['os-vol-host-attr:host'] || ''
              const volumeType = volumeHostAtt?.split('#')?.[1]
              const volumeTypeConfig = volumeHostAtt?.split('@')?.[1]?.split('#')?.[0]
              const volumeDriver = blueprintDetails?.[volumeType]?.[volumeTypeConfig]?.driver || ''
              return volumes?.length > 1
                ? VOLUME_ACTION_TOOLTIPS.EXTEND_SINGLE
                : volumes[0]?.status !== 'available' &&
                  !(volumes[0]?.status === 'in-use' && volumeDriver !== 'NFS')
                ? VOLUME_ACTION_TOOLTIPS.EXTEND_VOLUME_CONDITION
                : ''
            },
            BatchActionButton: getGridDialogButton(
              ExtendVolumeDialog,
              { reloadVolumes },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            cond: (volumes) => {
              if (!isAdmin()) {
                return false
              }
              return (
                volumes?.length === 1 &&
                ['available', 'in-use'].includes(volumes?.[0]?.status) &&
                // prettier-ignore
                [null, 'None', 'deleting', 'error', 'success'].includes(volumes?.[0]?.migration_status)
              )
            },
            icon: 'database',
            label: 'Migrate Volume',
            disabledTooltip: isAdmin()
              ? VOLUME_ACTION_TOOLTIPS.MIGRATE_VOLUME_CONDITION
              : VOLUME_ACTION_TOOLTIPS.MIGRATE_ADMIN_ONLY,
            BatchActionButton: getGridDialogButton(
              MigrateVolumeDialog,
              { reloadVolumes },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            cond: (volumes) => {
              if (!isAdmin() && volumes?.[0]?.user_id !== userId) {
                return false
              }
              return volumes?.length === 1 && ['available', 'in-use'].includes(volumes?.[0]?.status)
            },
            icon: 'swap',
            label: 'Retype Volume',
            disabledTooltip: isAdmin()
              ? VOLUME_ACTION_TOOLTIPS.RETYPE_AVAILABLE_IN_USE_STATUS
              : VOLUME_ACTION_TOOLTIPS.RETYPE_OWN_VOLUMES_CONDITION,
            BatchActionButton: getGridDialogButton(
              RetypeVolumeDialog,
              { reloadVolumes },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            cond: (volumes) =>
              volumes?.length === 1 &&
              volumes?.[0]?.status === 'available' &&
              volumes?.[0]?.['os-vol-tenant-attr:tenant_id'] === currentTenant,
            icon: 'arrow-right-arrow-left',
            label: 'Transfer Volume',
            disabledTooltip: VOLUME_ACTION_TOOLTIPS.TRANSFER_AVAILABLE_STATUS,
            BatchActionButton: getGridDialogButton(
              TransferVolumeDialog,
              { reloadVolumes },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            cond: (volumes) =>
              volumes?.length === 1 &&
              volumes?.[0]?.status === 'awaiting-transfer' &&
              volumes?.[0]?.['os-vol-tenant-attr:tenant_id'] === currentTenant,
            icon: 'ban',
            label: 'Cancel Transfer',
            disabledTooltip: (volumes) =>
              volumes?.length > 1
                ? VOLUME_ACTION_TOOLTIPS.CANCEL_TRANSFER_SINGLE
                : volumes[0]?.status !== 'awaiting-transfer'
                ? VOLUME_ACTION_TOOLTIPS.CANCEL_TRANSFER_AWAITING_STATUS
                : volumes?.[0]?.['os-vol-tenant-attr:tenant_id'] !== currentTenant
                ? VOLUME_ACTION_TOOLTIPS.CANCEL_TRANSFER_CURRENT_TENANT
                : '',
            BatchActionButton: getGridDialogButton(
              CancelTransferDialog,
              { reloadVolumes },
              {
                className: classes.dropdownAction,
              },
            ),
          },
          {
            cond: (volumes) => isAdmin() && volumes?.length === 1,
            icon: 'sliders-h',
            label: 'Set State',
            handleAction: (volumes) => {
              setSelectedVolume(volumes[0])
              setShowSetStateVolumeDialog(true)
            },
            keepRowsSelected: false,
          },
        ],
      },
    ],
    [classes, blueprintDetails, currentTenant, userId, reloadVolumes],
  )

  const rowMenuItems: Array<GridRowMenuItemSpec<SelectorModel>> = useMemo(
    () => [
      {
        label: 'Edit',
        icon: 'edit',
        handleClick: (volume) => {
          setSelectedVolume(volume)
          setShowEditVolumeDialog(true)
        },
        // cond: (selected) => {
        //   return ['shutoff', 'stopped'].includes(selected?.state)
        // },
        refreshAfterSuccess: true,
        hideIfDisabled: false,
      },
      {
        label: 'Edit Metadata',
        icon: 'tags',
        handleClick: (volume) => {
          setSelectedVolume(volume)
          setShowEditVolumeMetadataModal(true)
        },
        refreshAfterSuccess: true,
        hideIfDisabled: false,
      },
      {
        label: 'Snapshot',
        icon: 'camera',
        handleClick: (volume) => {
          setSelectedVolume(volume)
          setShowSnapshotVolumeDialog(true)
        },
        // cond: (selected) => {
        //   return ['shutoff', 'stopped'].includes(selected?.state)
        // },
        refreshAfterSuccess: true,
        hideIfDisabled: false,
      },
      {
        label: 'Delete',
        icon: 'trash-alt',
        handleClick: (volume) => {
          setSelectedVolume(volume)
          setShowDeleteVolumeDialog(true)
        },
        refreshAfterSuccess: true,
        hideIfDisabled: false,
      },
      {
        label: 'Upload as Image',
        icon: 'upload',
        handleClick: (volume) => {
          setSelectedVolume(volume)
          setShowUploadVolumeDialog(true)
        },
        refreshAfterSuccess: true,
        hideIfDisabled: false,
      },
      {
        label: 'Attach Volume',
        icon: 'plus-square',
        handleClick: (volume) => {
          setSelectedVolume(volume)
          setShowAttachVolumeDialog(true)
        },
        cond: (selected) => {
          return selected?.status === 'available'
        },
        disabledTooltip: (selected) =>
          selected?.status !== 'available' ? VOLUME_ACTION_TOOLTIPS.ATTACH_AVAILABLE_STATUS : '',
        refreshAfterSuccess: true,
        hideIfDisabled: false,
      },
      {
        label: 'Detach Volume',
        icon: 'minus-square',
        handleClick: (volume) => {
          setSelectedVolume(volume)
          setShowDetachVolumeDialog(true)
        },
        cond: (selected) => {
          return selected?.status === 'in-use'
        },
        disabledTooltip: (selected) =>
          selected?.status !== 'in-use' ? VOLUME_ACTION_TOOLTIPS.DETACH_IN_USE_STATUS : '',
        refreshAfterSuccess: true,
        hideIfDisabled: false,
      },
      {
        label: 'Extend Volume',
        icon: 'square-up',
        handleClick: (volume) => {
          setSelectedVolume(volume)
          setShowExtendVolumeDialog(true)
        },
        cond: (selected) => {
          const volumeHostAtt = selected['os-vol-host-attr:host'] || ''
          const volumeType = volumeHostAtt?.split('#')?.[1]
          const volumeTypeConfig = volumeHostAtt?.split('@')?.[1]?.split('#')?.[0]
          const volumeDriver = blueprintDetails?.[volumeType]?.[volumeTypeConfig]?.driver || ''

          return (
            selected?.status === 'available' ||
            (selected?.status === 'in-use' && volumeDriver !== 'NFS')
          )
        },
        disabledTooltip: (selected) => {
          const volumeHostAtt = selected['os-vol-host-attr:host'] || ''
          const volumeType = volumeHostAtt?.split('#')?.[1]
          const volumeTypeConfig = volumeHostAtt?.split('@')?.[1]?.split('#')?.[0]
          const volumeDriver = blueprintDetails?.[volumeType]?.[volumeTypeConfig]?.driver || ''
          return selected?.status !== 'available' &&
            !(selected?.status === 'in-use' && volumeDriver !== 'NFS')
            ? VOLUME_ACTION_TOOLTIPS.EXTEND_VOLUME_CONDITION
            : ''
        },
        refreshAfterSuccess: true,
        hideIfDisabled: false,
      },
      {
        label: 'Migrate Volume',
        icon: 'database',
        handleClick: (volume) => {
          setSelectedVolume(volume)
          setShowMigrateVolumeDialog(true)
        },
        cond: (selected) => {
          if (!isAdmin()) {
            return false
          }
          return (
            ['available', 'in-use'].includes(selected.status) &&
            // prettier-ignore
            [null, 'None', 'deleting', 'error', 'success'].includes(selected?.migration_status)
          )
        },
        disabledTooltip: (selected) => {
          if (!isAdmin()) {
            return VOLUME_ACTION_TOOLTIPS.MIGRATE_ADMIN_ONLY
          }
          return !(
            ['available', 'in-use'].includes(selected.status) &&
            // prettier-ignore
            [null, 'None', 'deleting', 'error', 'success'].includes(selected?.migration_status)
          )
            ? VOLUME_ACTION_TOOLTIPS.MIGRATE_VOLUME_CONDITION
            : ''
        },
        refreshAfterSuccess: true,
        hideIfDisabled: false,
      },
      {
        label: 'Retype Volume',
        icon: 'swap',
        handleClick: (volume) => {
          setSelectedVolume(volume)
          setShowRetypeVolumeDialog(true)
        },
        cond: (selected) => {
          if (!isAdmin() && selected.user_id !== userId) {
            return false
          }
          return ['available', 'in-use'].includes(selected.status)
        },
        disabledTooltip: (selected) => {
          if (!isAdmin() && selected.user_id !== userId) {
            return VOLUME_ACTION_TOOLTIPS.RETYPE_OWN_VOLUMES
          }
          return !['available', 'in-use'].includes(selected.status)
            ? VOLUME_ACTION_TOOLTIPS.RETYPE_AVAILABLE_IN_USE_STATUS
            : ''
        },
        refreshAfterSuccess: true,
        hideIfDisabled: false,
      },
      {
        label: 'Transfer Volume',
        icon: 'arrow-right-arrow-left',
        handleClick: (volume) => {
          setSelectedVolume(volume)
          setShowTransferVolumeDialog(true)
        },
        cond: (selected) => {
          return (
            selected.status === 'available' &&
            selected['os-vol-tenant-attr:tenant_id'] === currentTenant
          )
        },
        disabledTooltip: (selected) =>
          selected.status !== 'available'
            ? VOLUME_ACTION_TOOLTIPS.TRANSFER_AVAILABLE_STATUS
            : selected['os-vol-tenant-attr:tenant_id'] !== currentTenant
            ? VOLUME_ACTION_TOOLTIPS.TRANSFER_CURRENT_TENANT
            : '',
        refreshAfterSuccess: true,
        hideIfDisabled: false,
      },
      {
        label: 'Cancel Transfer',
        icon: 'ban',
        handleClick: (volume) => {
          setSelectedVolume(volume)
          setShowCancelTransferVolumeDialog(true)
        },
        cond: (selected) => {
          return (
            selected.status === 'awaiting-transfer' &&
            selected['os-vol-tenant-attr:tenant_id'] === currentTenant
          )
        },
        disabledTooltip: (selected) =>
          selected.status !== 'awaiting-transfer'
            ? VOLUME_ACTION_TOOLTIPS.CANCEL_TRANSFER_AWAITING_STATUS
            : selected['os-vol-tenant-attr:tenant_id'] !== currentTenant
            ? VOLUME_ACTION_TOOLTIPS.CANCEL_TRANSFER_CURRENT_TENANT
            : '',
        refreshAfterSuccess: true,
        hideIfDisabled: false,
      },
      {
        label: 'Set State',
        icon: 'sliders-h',
        cond: (selected) => isAdmin(),
        handleClick: (volume) => {
          setSelectedVolume(volume)
          setShowSetStateVolumeDialog(true)
        },
        refreshAfterSuccess: true,
      },
    ],
    [blueprintDetails, currentTenant, userId],
  )

  const columns = useMemo(() => volumeColumns({ hostsById, isAdmin, allTenants, setErrorVolume }), [
    hostsById,
    allTenants,
  ])

  const VOLUMES_FILTER_CONFIGS = useMemo(() => {
    return getVolumeFilterConfigs({ allTenants, tenants, clusters })
  }, [allTenants, tenants, clusters])

  return (
    <>
      <DocumentMeta title="Volumes" />
      <PollingData
        hidden
        key="poll-volumes"
        loading={polling}
        onReload={handlePoll}
        refreshDuration={1000 * 30}
      />
      <CreateVolumeModal
        addRoute={routeManager.routes.openstack.createVolume}
        reloadVolumes={reloadVolumes}
      />
      {showAcceptVolumeTransferDialog && (
        <AcceptVolumeTransferDialog
          onClose={() => setShowAcceptVolumeTransferDialog(false)}
          reloadVolumes={reloadVolumes}
        />
      )}
      {showEditVolumeDialog && (
        <EditVolumeDialog
          rows={[selectedVolume]}
          onClose={() => setShowEditVolumeDialog(false)}
          reloadVolumes={reloadVolumes}
        />
      )}
      {showSnapshotVolumeDialog && (
        <SnapshotVolumeDialog
          rows={[selectedVolume]}
          onClose={() => setShowSnapshotVolumeDialog(false)}
          // Don't need reloadVolumes here, routes to snapshots page
        />
      )}
      {showUploadVolumeDialog && (
        <UploadVolumeAsImageDialog
          rows={[selectedVolume]}
          onClose={() => setShowUploadVolumeDialog(false)}
          reloadVolumes={reloadVolumes}
        />
      )}
      {showAttachVolumeDialog && (
        <AttachVolumeDialog
          rows={[selectedVolume]}
          onClose={() => setShowAttachVolumeDialog(false)}
          reloadVolumes={reloadVolumes}
        />
      )}
      {showDetachVolumeDialog && (
        <DetachVolumeDialog
          rows={[selectedVolume]}
          onClose={() => setShowDetachVolumeDialog(false)}
          reloadVolumes={reloadVolumes}
        />
      )}
      {showExtendVolumeDialog && (
        <ExtendVolumeDialog
          rows={[selectedVolume]}
          onClose={() => setShowExtendVolumeDialog(false)}
          reloadVolumes={reloadVolumes}
        />
      )}
      {showRetypeVolumeDialog && (
        <RetypeVolumeDialog
          rows={[selectedVolume]}
          onClose={() => setShowRetypeVolumeDialog(false)}
          reloadVolumes={reloadVolumes}
        />
      )}
      {showTransferVolumeDialog && (
        <TransferVolumeDialog
          rows={[selectedVolume]}
          onClose={() => setShowTransferVolumeDialog(false)}
          reloadVolumes={reloadVolumes}
        />
      )}
      {showCancelTransferVolumeDialog && (
        <CancelTransferDialog
          rows={[selectedVolume]}
          onClose={() => setShowCancelTransferVolumeDialog(false)}
          reloadVolumes={reloadVolumes}
        />
      )}
      {showEditVolumeMetadataModal && (
        <EditVolumeMetadataModal
          rows={[selectedVolume]}
          onClose={() => setShowEditVolumeMetadataModal(false)}
          reloadVolumes={reloadVolumes}
        />
      )}
      {showMigrateVolumeDialog && (
        <MigrateVolumeDialog
          rows={[selectedVolume]}
          onClose={() => setShowMigrateVolumeDialog(false)}
          reloadVolumes={reloadVolumes}
        />
      )}
      {showDeleteVolumeDialog && (
        <DeleteVolumesDialog
          rows={[selectedVolume]}
          onClose={() => setShowDeleteVolumeDialog(false)}
          reloadVolumes={reloadVolumes}
        />
      )}

      {showSetStateVolumeDialog && (
        <SetStateVolumeDialog
          rows={[selectedVolume]}
          onClose={() => setShowSetStateVolumeDialog(false)}
          reloadVolumes={reloadVolumes}
        />
      )}
      {errorVolume && errorVolume?.status === 'error' && errorVolume?.id && (
        <VolumeErrorModal volume={errorVolume} onClose={() => setErrorVolume(null)} />
      )}
      {/* Server-side search, filters, and sort */}
      <ServerSideSearch
        searchFields={VOLUMES_SEARCH_FIELDS}
        filterConfigs={VOLUMES_FILTER_CONFIGS}
        sortOptions={VOLUMES_SORT_OPTIONS}
        onSearch={onSearch}
        onClear={onClear}
        isLoading={loadingVolumes}
        initialParams={{
          searchField: searchParams.searchField,
          searchValue: searchParams.searchValue,
          filters: searchParams.filters,
          sortKey: searchParams.sortKey,
          sortDir: searchParams.sortDir,
        }}
        defaultSortKey="created_at"
        defaultSortDir="desc"
        extraContent={
          <>
            {isAdmin() ? (
              <ToggleSwitch
                active={allTenants}
                className={classes.toggleSwitch}
                onClick={() => setAllTenants(!allTenants)}
                label="View Volumes in All Tenants"
              />
            ) : null}
          </>
        }
      />
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.OpenstackVolumes}
        searchTargets={[]}
        uniqueIdentifier="id"
        loading={loadingVolumes}
        onRefresh={reloadVolumes}
        data={volumes}
        columns={columns}
        addUrl={routeManager.routes.openstack.createVolume.path()}
        addText="Create New Volume"
        getParamsUpdater={getParamsUpdater}
        deleteAction={deleteVolume as any}
        batchActions={batchActions}
        dropdownBatchActions={dropdownBatchActions}
        rowMenuItems={rowMenuItems}
        DeleteDialogComponent={DeleteVolumesDialog}
        extraHeaderContent={
          <>
            {!isReadonlyUser() && (
              <CreateButton
                variant="secondary"
                onClick={() => setShowAcceptVolumeTransferDialog(true)}
              >
                Accept Volume Transfer
              </CreateButton>
            )}
          </>
        }
        iconOnly
        multiSelection
        // refreshing={allTenants ? refreshingAllVolumes : refreshingVolumes}
        // Server-side pagination
        disableSearch
        disableSorting
        disablePagination
        CustomPaginationComponent={() => <ServerSidePagination {...paginationProps} />}
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  dropdownAction: {
    background: theme.components.dropdown.background,
    border: 'none',
    '& .button-text': {
      color: theme.components.dropdown.color,
      justifyContent: 'start',
    },
  },
  statusCell: {
    textAlign: 'center',
    display: 'inline-block',
  },
  migrationStatusCell: {
    textAlign: 'left',
  },
  error: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  filters: {
    display: 'flex',
    gap: 8,
  },
  toggleSwitch: {
    marginBottom: '8px',
  },
}))
